import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  LayoutDashboard, Building2, Briefcase,
  BarChart2, FileText, LogOut, Shield, Users, GraduationCap
} from 'lucide-react';

const NAV = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/companies', icon: Building2, label: 'Companies' },
  { path: '/admin/managers', icon: Shield, label: 'Managers' },
  { path: '/admin/applications', icon: FileText, label: 'Applications' },
  { path: '/admin/users', icon: Users, label: 'Users & Bans' },
  { path: '/admin/jobs', icon: Briefcase, label: 'Job Listings' },
  { path: '/admin/stats', icon: BarChart2, label: 'Statistics' },
  { path: '/admin/tutors', icon: GraduationCap, label: 'Tutors' },
  { path: '/admin/lms', label: 'LMS Institutions', icon: Building2 }
];

export default function AdminLayout({ children }) {
  const { signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8FAFF' }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: 240, background: '#0F172A',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
      }}>
        {/* Logo */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <img
            src="/universe.png"
            alt="Universe"
            style={{ width: 42, height: 42, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div>
            <div style={{
              fontWeight: 800, fontSize: 14,
              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: 1,
            }}>UNIVERSE</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>
              Admin Portal
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {NAV.map(({ path, icon: Icon, label }) => {
            const active = location.pathname === path;
            return (
              <Link key={path} to={path} style={{
                display: 'flex', alignItems: 'center',
                gap: 10, padding: '10px 12px',
                borderRadius: 10, marginBottom: 2,
                background: active ? 'rgba(79,142,247,0.15)' : 'transparent',
                color: active ? '#4F8EF7' : '#64748B',
                fontWeight: active ? 700 : 500,
                fontSize: 13, textDecoration: 'none',
                transition: 'all 0.15s',
                borderLeft: active ? '3px solid #4F8EF7' : '3px solid transparent',
              }}
                onMouseEnter={e => {
                  if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                }}
                onMouseLeave={e => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}>
                <Icon size={17} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Sign out */}
        <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center',
            gap: 10, padding: '10px 12px',
            borderRadius: 10, background: 'transparent',
            border: 'none', color: '#EF4444',
            fontWeight: 600, fontSize: 13,
            cursor: 'pointer', width: '100%',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <LogOut size={17} />Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={{ marginLeft: 240, flex: 1, minHeight: '100vh', background: '#F8FAFF' }}>
        <div style={{
          background: '#fff', borderBottom: '1px solid #E2E8F0',
          padding: '14px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 50,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/universe.png" alt="Universe"
              style={{ height: 28, objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; }} />
            <span style={{ fontSize: 13, color: '#94A3B8' }}>
              admin.inncatechnologies.com
            </span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px',
            background: 'rgba(79,142,247,0.08)',
            borderRadius: 8, border: '1px solid rgba(79,142,247,0.2)',
          }}>
            <Shield size={14} color="#4F8EF7" />
            <span style={{ fontSize: 12, color: '#4F8EF7', fontWeight: 700 }}>
              Admin Access
            </span>
          </div>
        </div>
        <div style={{ padding: 32 }}>{children}</div>
      </main>
    </div>
  );
}